import axios, { AxiosError } from 'axios';
import { applyAuthTokenInterceptor, IAuthTokens, TokenRefreshRequest } from 'axios-jwt';
import { t } from 'i18next';
import { useAuthStore } from 'store/auth';

import { APIError, ErrorFields } from 'types';

import { API_BASE_URL } from './_baseUrl';

// Editing default axios settings
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: 'application/json',
    'Accept-Language': localStorage.getItem('i18nextLng') || 'en'
  },
  timeout: 60000 // 1min
});

// JWT token refresh func
const requestRefresh: TokenRefreshRequest = async (refreshToken: string): Promise<IAuthTokens | string> => {
  // Raw axios, not the instance above: `axios-jwt` calls this outside the instance's config, so the
  // base has to be spelled out for the refresh to follow the same host as every other call.
  const accessToken = await axios
    .post(`${API_BASE_URL}/auth/token/refresh`, { refresh: refreshToken })
    .then(r => r.data.access)
    .catch(() => '');

  if (!accessToken) {
    useAuthStore.getState().logoutUser(true);
    return '';
  }

  return accessToken ? { accessToken, refreshToken } : '';
};

/**
 * Endpoints known to answer with a bare string body instead of our JSON error shape: the
 * GoPhish-backed phishing create/update/test calls relay their upstream validation text verbatim.
 * Only these get the string passthrough below, so an unexpected plain-text body from anywhere else
 * in the API (an internal message, a truncated stack, a gateway's own wording) is not shown to the
 * user just because it happened to be short.
 */
const PLAIN_TEXT_ERROR_PATHS = [/\/phishing\//];

const bodyIsPassThroughText = (url: string | undefined, body: unknown): body is string => {
  if (typeof body !== 'string') return false;
  if (!url || !PLAIN_TEXT_ERROR_PATHS.some(pattern => pattern.test(url))) return false;
  const trimmed = body.trim();
  // Reject empty bodies, HTML/XML documents (proxy 502/504 pages) and anything long enough to be a
  // stack dump rather than a message.
  return trimmed !== '' && !trimmed.startsWith('<') && trimmed.length <= 300;
};

// A pydantic 422 sends `detail` as a list of error objects, whose default stringification is
// "[object Object]"; join their messages so the toast carries the real reason.
const formatErrorDetail = (detail: ErrorFields['detail']): string =>
  Array.isArray(detail) ? detail.map(entry => entry.msg ?? JSON.stringify(entry)).join('; ') : detail.toString();

// Monarch refusing over its own certificates reaches us as a bare 500 "Internal server error"; only the code says why.
const UPSTREAM_CERT_CODE_KEYS: Record<string, string> = {
  UpstreamCertificateExpired: 'admin.mdm.upstreamCert.expired',
  UpstreamNotProvisioned: 'admin.mdm.upstreamCert.notProvisioned'
};

// Handling Response Errors
export const handleError = (error: AxiosError<ErrorFields>): APIError => {
  // If true, we got error message from API,
  // otherwise we got standard error message
  if (error.response !== undefined) {
    // `AxiosError<ErrorFields>` types the body as our JSON error shape, but it is not guaranteed
    // to be one at runtime (proxy 502/504 HTML pages and plain-text 500s arrive as strings), so
    // widen to `unknown` before narrowing. Guarding here matters: throwing inside a
    // `.catch(err => reject(handleError(err)))` would leave the caller's Promise pending forever.
    const body: unknown = error.response.data;

    if (bodyIsPassThroughText(error.config?.url, body)) {
      return {
        code: error.response.status,
        description: body.trim()
      };
    }

    const data = typeof body === 'object' && body !== null ? (body as ErrorFields) : null;
    const certKey = data?.code ? UPSTREAM_CERT_CODE_KEYS[data.code] : undefined;

    return {
      code: error.response.status,
      code_name: data?.code,
      description: certKey ? t(certKey) : data?.detail != null ? formatErrorDetail(data.detail) : error.message,
      ...(Array.isArray(data?.client_names) && { client_names: data.client_names, other_count: data.other_count ?? 0 }),
      ...(Array.isArray(data?.objects) && { objects: data.objects })
    };
  }

  return {
    code: error.code ? parseInt(error.code, 10) : 0,
    description: error.message
  };
};

// Applying the JWT token interceptor into axios instance
applyAuthTokenInterceptor(axiosInstance, {
  requestRefresh
});

// Cache-busting query params for GET requests that must always bypass the browser HTTP cache.
//
// Even though React Query is configured with staleTime/cacheTime 0 and refetchOnMount: 'always',
// a full page reload re-fetches through axios and the browser can still serve a stale cached
// response (heuristic / stale-while-revalidate caching keyed on the URL). Appending a unique
// value makes every request URL distinct, so no previously cached response is ever reused.
// A query param is used instead of a `Cache-Control` request header to avoid a CORS preflight
// change on the backend. Unknown query params are ignored by the API.
export const noCacheParams = (): { _: number } => ({ _: Date.now() });

export default axiosInstance;
